/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { isFunction } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { AsyncOverlay, Document, MessageBar } from '@zapier/common-components';
import { useLink } from '@zapier/common-hooks';
import { SkeletonBlock } from '@zapier/design-system';
import { usePaymentSource, PaymentApp } from '@zapier/payment';
import { usePromoPlans } from '@zapier/promotions';
import { Border, Colors } from '@zapier/theme';

import {
  useMaybeSendFullStoryEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import {
  buildDowngradeWarningUrl,
  isFree,
} from '@zapier/design-system-revenue/src/utils';

import {
  useChangePlan,
  useCurrentAccountId,
  usePlans,
  usePendingPayment,
} from '@zapier/design-system-revenue/src/hooks';

import {
  ChangeType,
  Interval,
  PlanV2,
} from '@zapier/design-system-revenue/src/types';

import {
  ChangePlanCompleteModal,
  PendingPaymentWarningBar,
} from '@zapier/design-system-revenue/src/components/other';

import { BillingOptions } from './BillingOptions';
import { ChangePlanButtons } from './ChangePlanButtons';
import { ChangePlanHeader } from './ChangePlanHeader';
import { ChangePlanIndicator } from './ChangePlanIndicator';
import { ChangePlanSection } from './ChangePlanSection';
import { ChangePlanSummary } from './ChangePlanSummary';

import { getChangeLabel, getChangeLabelForPlanName } from './utils';

type Props = {
  /** `upgrade`, `downgrade` etc. */
  changeType: ChangeType;
  /** The interval selected. Should be used when controlled via `onSelectInterval` */
  interval?: Interval;
  /** Hides the header */
  isCompact?: boolean;
  /** Called when cancel button is clicked */
  onCancel: () => void;
  /** Called when change plan action is successful */
  onFinishChangePlan?: () => void;
  /** Called when interval is clicked. Passing a function here makes this a controlled component */
  onSelectInterval?: (interval: Interval) => void;
  /** Flag to indicate whether to route to downgrade confirmation page before downgrading */
  showDowngradeConfirmation?: boolean;
  /** Flag to indicate whether to show the change plan success modal or not */
  showSuccessModal: boolean;
  /** THe name of the plan changing to */
  toPlanName: string;
};

const changePlanCss = (props: Props) =>
  css([
    css`
      background-color: ${Colors.white};
      border: ${Border.size.default} solid ${Border.color.default};
      border-radius: 5px;
      margin: 2em auto;
      max-width: 800px;
      padding: 2em;
      width: auto;
    `,
    props.isCompact &&
      css`
        border: none;
      `,
  ]);

const buttonsCss = css`
  margin-top: 2em;
`;

const errorCss = css`
  margin-top: 18px;
  text-align: center;
`;

const findPlans = (
  toPlanName: string,
  plans: PlanV2[],
  currentInterval: Interval
) => {
  const altInterval = currentInterval === 'year' ? 'month' : 'year';
  const toPlanNameLower = toPlanName.toLowerCase();

  let toPlan = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() && currentInterval === p.interval
  );

  toPlan =
    toPlan ??
    plans.find(
      p =>
        toPlanNameLower === p.name.toLowerCase() && altInterval === p.interval
    );

  // The alternative interval plan for the selected plan
  const toPlanAlt = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() &&
      p.tasks === toPlan?.tasks &&
      p.zaps === toPlan?.zaps &&
      p.interval !== toPlan.interval
  );

  return [toPlan, toPlanAlt];
};

export const ChangePlan = (props: Props) => {
  const {
    toPlanName,
    changeType,
    interval,
    onSelectInterval,
    showSuccessModal,
  } = props;
  const { routeTo } = useLink();
  const { trackEvent } = useTrackingContext();

  const [selectedInterval, setSelectedInterval] = useState('year' as Interval);
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  const { accountId } = useCurrentAccountId();
  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
  });
  const { pendingInvoice } = usePendingPayment();

  const {
    data: { plans, currentAccount, currentPlan },
    isLoading: isLoadingPlans,
  } = usePlans(accountId, { flatPlans: true });

  const { isLoading: isLoadingPromoPlans, promoPlans } = usePromoPlans();

  const isLoading = isLoadingPlans || isLoadingPromoPlans;
  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === 'upgrade',
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  const {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
    pausedZaps,
  } = useChangePlan({
    accountId,
    planId: toPlan?.id,
    downgradeReason: null,
  });

  const onChangePlan = async () => {
    if (
      changeType === 'downgrade' &&
      props.showDowngradeConfirmation &&
      routeTo
    ) {
      return routeTo(
        buildDowngradeWarningUrl(
          currentPlan?.name ?? '',
          toPlanName,
          currentInterval
        )
      );
    }

    await changePlan();

    trackEvent('Billing: plan changed', {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent('Billing: cancelChangePlan', {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: 'first step',
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const changeLabel = getChangeLabel(changeType);
  const heading = getChangeLabelForPlanName(changeType, toPlan?.name ?? '');
  const subheading = `${changeLabel} account`;

  const submitText =
    changeType === 'change'
      ? `${changeLabel} Account Billing`
      : `${changeLabel} My Account`;

  const hasNoPaymentSource =
    !isFree(toPlan) && (!primarySource || forceSourceEdit);

  const isSubmitDisabled = hasNoPaymentSource || pendingInvoice;

  const popoverText = hasNoPaymentSource
    ? 'Please add a payment method.'
    : pendingInvoice
    ? 'Cannot change plan with a pending payment.'
    : null;

  return (
    <Document title={heading}>
      {isLoading ? (
        <SkeletonBlock />
      ) : (
        <Fragment>
          {showSuccessModal && hasChangedPlan && (
            <ChangePlanCompleteModal
              changeType={changeType}
              hasPausedZaps={pausedZaps.length > 0}
              newPlan={toPlan as PlanV2}
            />
          )}
          <AsyncOverlay
            isVisible={isChangingPlan}
            renderAsyncIndicator={
              isChangingPlan ? () => <ChangePlanIndicator /> : undefined
            }
          >
            <form css={changePlanCss(props)} onSubmit={e => e.preventDefault()}>
              {!props.isCompact && (
                <ChangePlanHeader
                  accountName={
                    currentAccount?.owner?.email ?? currentAccount?.name
                  }
                  companyName={currentAccount?.companyName}
                  heading={heading}
                  subheading={subheading}
                />
              )}

              {pendingInvoice && (
                <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
              )}

              <BillingOptions
                onSelectOption={onSelectIntervalCallback}
                planOptions={([toPlan, toPlanAlt] as PlanV2[])
                  .sort((a, b) => b.amount - a.amount)
                  .filter(p => p !== undefined)}
                selectedOption={currentInterval}
              />

              {!isFree(toPlan) && (
                <ChangePlanSection title="Payment Details">
                  <PaymentApp editInModal={true} />
                </ChangePlanSection>
              )}

              <ChangePlanSection title="Summary">
                {toPlan && (
                  <ChangePlanSummary
                    accountId={currentAccount?.id as number}
                    plan={toPlan}
                  />
                )}
              </ChangePlanSection>

              <div css={buttonsCss}>
                <ChangePlanButtons
                  isDisabled={isSubmitDisabled}
                  onCancelChangePlan={onCancelChange}
                  onChangePlan={onChangePlan}
                  popoverText={popoverText}
                  submitText={submitText}
                />
              </div>
              {changePlanError && (
                <div css={errorCss}>
                  <MessageBar type="error">{changePlanError}</MessageBar>
                </div>
              )}
            </form>
          </AsyncOverlay>
        </Fragment>
      )}
    </Document>
  );
};
